import { useState, useEffect } from 'react'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  Package,
  MagnifyingGlass,
  Boat,
  Airplane,
  Truck,
  MapPin,
  ArrowRight,
  DownloadSimple,
  Eye,
  Clock,
  Spinner,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'

interface Order {
  id: string
  status: string
  trackingNumber?: string
  shippedAt?: string | null
  estimatedDelivery?: string
  actualDelivery?: string
  createdAt: string
  updatedAt: string
  quoteTxControl: string
  origin: { location?: { city?: string; country?: string } }
  destination: { location?: { city?: string; country?: string } }
  modalityName: string
  shipperName: string
  shipperTradingName?: string
  shipperLogoUrl?: string
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  currency: string
}

export default function ShipmentsPage() {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  useEffect(() => {
    async function fetchOrders() {
      try {
        setLoading(true)
        setError(null)
        const res = await api.get('/freight-forwarder/orders')
        setOrders(res.data.orders || [])
      } catch (err: any) {
        console.error('Error fetching orders:', err)
        setError(err.response?.data?.error || 'Erro ao carregar embarques')
      } finally {
        setLoading(false)
      }
    }
    fetchOrders()
  }, [])

  const formatCurrency = (value: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
    }).format(value)
  }

  const getModalityIcon = (modalityName: string) => {
    const name = modalityName?.toLowerCase() || ''
    if (name.includes('mar')) return <Boat size={18} className="text-blue-600" weight="duotone" />
    if (name.includes('aer') || name.includes('air')) return <Airplane size={18} className="text-purple-600" weight="duotone" />
    if (name.includes('ter') || name.includes('rod')) return <Truck size={18} className="text-green-600" weight="duotone" />
    return <Package size={18} className="text-gray-600" />
  }

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; color: string }> = {
      pending: { label: 'Aprovado', color: 'bg-emerald-100 text-emerald-700' },
      processing: { label: 'Processando', color: 'bg-blue-100 text-blue-700' },
      in_transit: { label: 'Em Trânsito', color: 'bg-indigo-100 text-indigo-700' },
      delivered: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
      canceled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
    }
    const { label, color } = config[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    )
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.quoteTxControl?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.shipperName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.trackingNumber?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = filterStatus === 'all' || order.status === filterStatus

    return matchesSearch && matchesStatus
  })

  // Calculate stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    inTransit: orders.filter(o => o.status === 'in_transit').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Meus Embarques
              </h1>
              <p className="text-sm text-gray-500">
                Propostas aceitas e pedidos em andamento
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Carregando embarques...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500">Em Trânsito</p>
                <p className="text-2xl font-bold text-blue-600">{stats.inTransit}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500">Entregues</p>
                <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <MagnifyingGlass
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <Input
                    type="text"
                    placeholder="Buscar por código, cliente ou rastreamento..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-9 border-gray-200"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">Todos Status</option>
                  <option value="pending">Pendente</option>
                  <option value="processing">Processando</option>
                  <option value="in_transit">Em Trânsito</option>
                  <option value="delivered">Entregue</option>
                  <option value="canceled">Cancelado</option>
                </select>
              </div>
            </div>

            {/* Empty State */}
            {filteredOrders.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <Package size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhum embarque encontrado
                </h3>
                <p className="text-gray-500 mb-4">
                  {orders.length === 0
                    ? 'Quando uma proposta for aceita, o embarque aparecerá aqui.'
                    : 'Nenhum embarque corresponde aos filtros selecionados.'}
                </p>
                {orders.length === 0 && (
                  <Button onClick={() => navigate('/freight-forwarder/opportunities')}>
                    Ver Oportunidades
                  </Button>
                )}
              </div>
            )}

            {/* Orders Table */}
            {filteredOrders.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pedido
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rota
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ETD / ETA
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredOrders.map((order) => (
                        <tr
                          key={order.id}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/freight-forwarder/orders/${order.id}`)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {getModalityIcon(order.modalityName)}
                              <div>
                                <p className="font-medium text-sm text-gray-900">
                                  {order.quoteTxControl}
                                </p>
                                {order.trackingNumber && (
                                  <p className="text-xs text-gray-500 font-mono">
                                    {order.trackingNumber}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-900">
                              {order.shipperTradingName || order.shipperName}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MapPin size={14} className="text-gray-400" />
                              <span>{order.origin?.location?.city || 'Origem'}</span>
                              <ArrowRight size={12} className="text-gray-400" />
                              <span>{order.destination?.location?.city || 'Destino'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="px-4 py-3">
                            <MultiCurrencyTotal
                              totalsByCurrency={totalsFromLegacyOffer(order)}
                              variant="compact"
                              align="start"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm">
                              <div className="flex items-center gap-1 text-gray-700">
                                <span className="text-xs text-gray-400 w-8">ETD</span>
                                <span>{order.shippedAt ? formatDate(order.shippedAt) : '—'}</span>
                              </div>
                              <div className="flex items-center gap-1 text-gray-700">
                                <span className="text-xs text-gray-400 w-8">ETA</span>
                                <span>{order.estimatedDelivery ? formatDate(order.estimatedDelivery) : '—'}</span>
                                {order.estimatedDelivery && (() => {
                                  const days = Math.ceil((new Date(order.estimatedDelivery).getTime() - Date.now()) / 86400000)
                                  return <span className={`text-xs ${days < 0 ? 'text-red-600' : 'text-gray-500'}`}>({days < 0 ? `${-days}d atraso` : `faltam ${days}d`})</span>
                                })()}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/freight-forwarder/orders/${order.id}`)
                              }}
                              className="h-8 w-8 p-0"
                              title="Ver Detalhes"
                            >
                              <Eye size={16} className="text-gray-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{filteredOrders.length}</span> embarque{filteredOrders.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </AppLayout>
  )
}
